/**
 * TrackerView — the main composition surface.
 *
 * Phase 1 step 6: read-only view of the demo song with a moving playhead.
 * Phase 1 step 7: keyboard-driven editing. Z/Q rows enter notes, arrow
 *                 keys move the cursor, edit mode auto-advances rows.
 *
 * The container has `tabIndex={0}` so it captures focus and keystrokes
 * don't escape to the browser. Click the grid (or anywhere inside) to
 * grab focus.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createEngine,
  DEMO_SONG,
  type DemoRow,
  type DemoSong,
  type EngineHandle,
  type PreviewChannel,
} from '../audio';
import { useAutosave } from '../hooks/useAutosave';
import {
  replaceChannelInSong,
  type ChannelEvent,
  type EventChannel,
} from '../lib/channel-events';
import { CHANNEL_ORDER, mapKeyToAction, type ChannelName } from '../lib/keyboard';
import {
  insertPresetAtRow,
  type PatternPreset,
} from '../lib/pattern-presets';
import { loadAutosave } from '../lib/persistence';
import {
  addOrderEntry,
  addPattern,
  createEmptySong,
  cyclePatternAtOrder,
  removeOrderEntry,
} from '../lib/song-edit';
import { AIPanel } from './AIPanel';
import { FileMenu } from './FileMenu';
import { OrderList } from './OrderList';
import { PatternGrid } from './PatternGrid';
import { PatternLibrary } from './PatternLibrary';

const POLL_MS = 50;
const MIN_OCTAVE = 1;
const MAX_OCTAVE = 7;

function cloneSong(s: DemoSong): DemoSong {
  return {
    ...s,
    patterns: s.patterns.map((pat) => pat.map((row) => ({ ...row }))),
    orderList: [...s.orderList],
  };
}

export function TrackerView() {
  const containerRef = useRef<HTMLElement>(null);
  const engineRef = useRef<EngineHandle | null>(null);

  // Editable song state (seeded from the demo; replaced if an auto-save exists).
  const [song, setSong] = useState<DemoSong>(() => cloneSong(DEMO_SONG));

  // On first mount, see if there's an auto-saved session and offer to restore.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await loadAutosave();
        if (cancelled || !snap) return;
        // Only prompt if the autosave isn't the demo we just seeded with.
        if (snap.song.title === DEMO_SONG.title && snap.song.patterns.length === DEMO_SONG.patterns.length) {
          // probably the demo or a near-clone — restore silently
          setSong(snap.song);
          return;
        }
        if (window.confirm(`Restore auto-saved session "${snap.song.title}"?`)) {
          setSong(snap.song);
        }
      } catch {
        /* ignore — autosave is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const autosave = useAutosave(song);

  // Playback state
  const [playing, setPlaying] = useState(false);
  const [orderIndex, setOrderIndex] = useState<number | null>(null);
  const [rowInPattern, setRowInPattern] = useState<number | null>(null);
  const [tempo, setTempoState] = useState<number>(150);

  // Cursor + edit state
  const [cursorRow, setCursorRow] = useState<number>(0);
  const [cursorChannel, setCursorChannel] = useState<number>(0);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState<number>(0);
  const [octave, setOctave] = useState<number>(4);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  // Auto-follow the playhead while playing — selection tracks the song.
  useEffect(() => {
    if (orderIndex !== null) setSelectedOrderIndex(orderIndex);
  }, [orderIndex]);

  const currentPatternIndex = useMemo(
    () => song.orderList[selectedOrderIndex] ?? song.orderList[0]!,
    [song.orderList, selectedOrderIndex],
  );
  const visiblePattern = song.patterns[currentPatternIndex] ?? [];

  function setTempo(next: number) {
    setTempoState(next);
    engineRef.current?.setTempo(6, next);
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      engineRef.current?.destroy();
      engineRef.current = null;
    };
  }, []);

  // Poll engine playhead while a song is playing
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      const eng = engineRef.current;
      if (!eng) return;
      if (!eng.isPlaying()) {
        setPlaying(false);
        setOrderIndex(null);
        setRowInPattern(null);
        return;
      }
      const ph = eng.getPlayhead();
      if (ph) {
        setOrderIndex(ph.orderIndex);
        setRowInPattern(ph.rowInPattern);
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [playing]);

  /** Start from a specific position, or stop if already playing. */
  const playFrom = useCallback(
    async (from: { orderIndex: number; rowInPattern: number }) => {
      try {
        setError(null);
        await ensureEngine().playDemo(song, 6, tempo, {
          fromOrderIndex: from.orderIndex,
          fromRow: from.rowInPattern,
        });
        setPlaying(true);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
        setPlaying(false);
      }
    },
    [song, tempo],
  );

  const stopPlayback = useCallback(() => {
    engineRef.current?.stop();
    setPlaying(false);
    setOrderIndex(null);
    setRowInPattern(null);
  }, []);

  const toggle = useCallback(async () => {
    if (playing) {
      stopPlayback();
      return;
    }
    await playFrom({ orderIndex: selectedOrderIndex, rowInPattern: cursorRow });
  }, [playing, stopPlayback, playFrom, selectedOrderIndex, cursorRow]);

  const playFromStart = useCallback(async () => {
    if (playing) stopPlayback();
    await playFrom({ orderIndex: 0, rowInPattern: 0 });
  }, [playing, stopPlayback, playFrom]);

  // Push live song updates to the engine so cell edits are heard immediately.
  useEffect(() => {
    engineRef.current?.updateSong(song);
  }, [song]);

  // Apply a cell mutation to the visible pattern at the current cursor row.
  const updateCell = useCallback(
    (mutator: (row: DemoRow) => DemoRow) => {
      setSong((prev) => {
        const next = cloneSong(prev);
        const pat = next.patterns[currentPatternIndex];
        if (!pat) return prev;
        const target = pat[cursorRow];
        if (!target) return prev;
        pat[cursorRow] = mutator(target);
        return next;
      });
    },
    [currentPatternIndex, cursorRow],
  );

  function ensureEngine(): EngineHandle {
    if (!engineRef.current) engineRef.current = createEngine();
    return engineRef.current;
  }

  const previewNote = useCallback((channel: PreviewChannel, note: string) => {
    const eng = ensureEngine();
    void eng.preview(channel, note).catch(() => {
      /* preview is best-effort; ignore failures (e.g. context locked) */
    });
  }, []);

  const writeNote = useCallback(
    (note: string | null) => {
      const channel = CHANNEL_ORDER[cursorChannel];
      if (!channel) return;
      if (channel === 'dpcm') return; // DPCM editing waits for instrument support
      updateCell((row) => {
        if (channel === 'pulse1' || channel === 'pulse2') {
          return {
            ...row,
            [channel]: {
              ...(row[channel] ?? {}),
              note,
              // Default to the demo's classic-lead duty if entering fresh.
              duty: row[channel]?.duty ?? 1,
              volume: row[channel]?.volume ?? 12,
            },
          };
        }
        if (channel === 'triangle') {
          return { ...row, triangle: { note } };
        }
        if (channel === 'noise') {
          if (note === null) return { ...row, noise: { ...(row.noise ?? {}), note: null } };
          if (note === 'OFF') return { ...row, noise: { note: 'OFF' } };
          // Map note input to "active" marker; keep prior period/mode.
          return {
            ...row,
            noise: {
              ...(row.noise ?? {}),
              note: 'on',
              periodIndex: row.noise?.periodIndex ?? 5,
              mode: row.noise?.mode ?? 'long',
              volume: row.noise?.volume ?? 8,
            },
          };
        }
        return row;
      });
    },
    [cursorChannel, updateCell],
  );

  const clearCell = useCallback(() => {
    const channel = CHANNEL_ORDER[cursorChannel];
    if (!channel) return;
    updateCell((row) => {
      const next = { ...row };
      delete (next as Record<ChannelName, unknown>)[channel];
      return next;
    });
  }, [cursorChannel, updateCell]);

  const moveCursor = useCallback(
    (dRow: number, dChannel: number) => {
      if (dRow !== 0) {
        setCursorRow((r) =>
          Math.max(0, Math.min(song.rowsPerPattern - 1, r + dRow)),
        );
      }
      if (dChannel !== 0) {
        setCursorChannel((c) => Math.max(0, Math.min(CHANNEL_ORDER.length - 1, c + dChannel)));
      }
    },
    [song.rowsPerPattern],
  );

  // Song-structure actions
  const handleSelectOrder = useCallback((idx: number) => {
    setSelectedOrderIndex(idx);
    setCursorRow(0);
  }, []);

  const handleCyclePattern = useCallback(
    (delta: number) => {
      setSong((prev) => cyclePatternAtOrder(prev, selectedOrderIndex, delta));
    },
    [selectedOrderIndex],
  );

  const handleAddRow = useCallback(() => {
    setSong((prev) => {
      const last = prev.orderList[prev.orderList.length - 1] ?? 0;
      const next = addOrderEntry(prev, last);
      // Auto-select the newly added entry.
      setSelectedOrderIndex(next.orderList.length - 1);
      setCursorRow(0);
      return next;
    });
  }, []);

  const handleRemoveRow = useCallback(() => {
    setSong((prev) => {
      if (prev.orderList.length <= 1) return prev;
      const next = removeOrderEntry(prev, selectedOrderIndex);
      setSelectedOrderIndex(Math.min(selectedOrderIndex, next.orderList.length - 1));
      setCursorRow(0);
      return next;
    });
  }, [selectedOrderIndex]);

  const handleAddPattern = useCallback(() => {
    setSong((prev) => addPattern(prev));
  }, []);

  const handleLoadSong = useCallback((loaded: DemoSong) => {
    setSong(loaded);
    setSelectedOrderIndex(0);
    setCursorRow(0);
    setCursorChannel(0);
    stopPlayback();
  }, [stopPlayback]);

  const handleNewSong = useCallback(() => {
    // 64 rows = 4 bars at 4 rows/beat. Matches the FT2 / FamiTracker default.
    setSong(createEmptySong('Untitled', 64));
    setSelectedOrderIndex(0);
    setCursorRow(0);
    setCursorChannel(0);
    stopPlayback();
  }, [stopPlayback]);

  // Pattern library modal state
  const [libraryOpen, setLibraryOpen] = useState(false);

  const handleInsertPreset = useCallback(
    (preset: PatternPreset, atRow: number, transposeSemitones: number, loop: boolean) => {
      setSong((prev) =>
        insertPresetAtRow(prev, currentPatternIndex, atRow, preset, transposeSemitones, loop),
      );
      setLibraryOpen(false);
    },
    [currentPatternIndex],
  );

  // AI panel state
  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  const handleAIApply = useCallback(
    (channel: EventChannel, events: ChannelEvent[]) => {
      setSong((prev) => replaceChannelInSong(prev, currentPatternIndex, channel, events));
    },
    [currentPatternIndex],
  );

  // Keyboard handler — listens only while the container has focus.
  // Edit mode OFF: typing previews the note (auditory feedback) but does NOT
  // mutate the pattern. Edit mode ON: previews + writes the cell + auto-advances.
  useEffect(() => {
    if (!focused) return;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey) return; // let cmd-c etc through
      const action = mapKeyToAction(e.key, { octave });
      if (!action) return;
      e.preventDefault();
      const channel = CHANNEL_ORDER[cursorChannel];
      switch (action.type) {
        case 'note':
          if (channel && channel !== 'dpcm') {
            previewNote(channel, action.note);
          }
          if (editMode) {
            writeNote(action.note);
            moveCursor(1, 0);
          }
          break;
        case 'noteOff':
          if (editMode) {
            writeNote('OFF');
            moveCursor(1, 0);
          }
          break;
        case 'clearCell':
          if (editMode) {
            clearCell();
            moveCursor(1, 0);
          }
          break;
        case 'moveCursor':
          moveCursor(action.dRow, action.dChannel);
          break;
        case 'octave':
          setOctave((o) => Math.max(MIN_OCTAVE, Math.min(MAX_OCTAVE, o + action.delta)));
          break;
        case 'transport':
          void toggle();
          break;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    focused,
    octave,
    editMode,
    cursorChannel,
    previewNote,
    writeNote,
    clearCell,
    moveCursor,
    toggle,
  ]);

  return (
    <section
      ref={containerRef}
      tabIndex={0}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={() => containerRef.current?.focus()}
      className={`flex w-full max-w-4xl flex-col gap-3 rounded outline-none ${
        focused ? 'ring-2 ring-nes-cyan/40 ring-offset-2 ring-offset-nes-black' : ''
      }`}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 rounded border border-nes-cyan/30 bg-nes-dim px-4 py-3">
        <div className="flex min-w-0 flex-col gap-2">
          <h2 className="text-base font-bold text-nes-cyan">{song.title}</h2>
          <p className="text-xs text-nes-white/50">
            {song.patterns.length} patterns · {song.rowsPerPattern} rows ·{' '}
            {focused ? 'focused — keyboard active' : 'click to focus'}
          </p>
          <FileMenu
            song={song}
            onLoadSong={handleLoadSong}
            onNewSong={handleNewSong}
            lastAutosavedAt={autosave.lastSavedAt}
            autosavePending={autosave.pending}
          />
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-nes-white/70">
            Octave
            <button
              onClick={() => setOctave((o) => Math.max(MIN_OCTAVE, o - 1))}
              className="rounded border border-nes-cyan/30 px-2 text-nes-cyan hover:bg-nes-cyan/10"
            >
              −
            </button>
            <span className="w-4 text-center tabular-nums text-nes-cyan">{octave}</span>
            <button
              onClick={() => setOctave((o) => Math.min(MAX_OCTAVE, o + 1))}
              className="rounded border border-nes-cyan/30 px-2 text-nes-cyan hover:bg-nes-cyan/10"
            >
              +
            </button>
          </label>

          <button
            onClick={() => setEditMode((v) => !v)}
            className={`rounded px-3 py-1 text-xs font-bold transition ${
              editMode
                ? 'bg-nes-red text-nes-white hover:bg-nes-red/80'
                : 'border border-nes-white/30 text-nes-white/70 hover:border-nes-white/60'
            }`}
          >
            {editMode ? '● REC' : 'edit off'}
          </button>

          <label className="flex items-center gap-2 text-xs text-nes-white/70">
            Tempo
            <input
              type="range"
              min={80}
              max={220}
              value={tempo}
              onChange={(e) => setTempo(parseInt(e.target.value, 10))}
              className="accent-nes-cyan"
            />
            <span className="w-12 text-right tabular-nums text-nes-cyan">{tempo}</span>
          </label>

          <button
            onClick={() => setLibraryOpen(true)}
            title="Browse pattern library — insert presets or save current as preset"
            className="rounded border border-nes-cyan/40 px-2 py-2 text-sm text-nes-cyan hover:bg-nes-cyan/10"
          >
            🎵 Library
          </button>
          <button
            onClick={() => setAiPanelOpen(true)}
            title="AI: generate a channel for the current pattern"
            className="rounded border border-nes-cyan/40 px-2 py-2 text-sm text-nes-cyan hover:bg-nes-cyan/10"
          >
            🤖 AI
          </button>
          <button
            onClick={playFromStart}
            title="Play from order 00 row 00"
            className="rounded border border-nes-cyan/40 px-2 py-2 text-sm text-nes-cyan hover:bg-nes-cyan/10"
          >
            ⏮
          </button>
          <button
            onClick={toggle}
            title={playing ? 'Stop' : 'Play from cursor'}
            className={`rounded px-4 py-2 text-sm font-bold transition ${
              playing
                ? 'bg-nes-red text-nes-white hover:bg-nes-red/80'
                : 'bg-nes-cyan text-nes-black hover:bg-nes-cyan/80'
            }`}
          >
            {playing ? '■ Stop' : `▶ Play from ${selectedOrderIndex.toString().padStart(2, '0')}.${cursorRow.toString().padStart(2, '0')}`}
          </button>
        </div>
      </header>

      <div className="flex gap-3">
        <OrderList
          orderList={song.orderList}
          currentOrderIndex={orderIndex}
          selectedOrderIndex={selectedOrderIndex}
          patternCount={song.patterns.length}
          onSelect={handleSelectOrder}
          onCyclePattern={handleCyclePattern}
          onAddRow={handleAddRow}
          onRemoveRow={handleRemoveRow}
          onAddPattern={handleAddPattern}
          playing={playing}
        />
        <div className="flex-1">
          <PatternGrid
            rows={visiblePattern}
            patternIndex={currentPatternIndex}
            currentRow={orderIndex === selectedOrderIndex ? rowInPattern : null}
            cursorRow={focused ? cursorRow : null}
            cursorChannel={focused ? cursorChannel : null}
            editMode={editMode}
          />
        </div>
      </div>

      <PatternLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        currentPatternRows={visiblePattern}
        rowsPerPattern={song.rowsPerPattern}
        cursorRow={cursorRow}
        onInsert={handleInsertPreset}
      />

      <AIPanel
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        song={song}
        patternIndex={currentPatternIndex}
        tempo={tempo}
        onApply={handleAIApply}
      />

      {error && <p className="text-sm text-nes-red">Error: {error}</p>}

      <details className="rounded border border-nes-cyan/20 bg-nes-dim/50 p-3 text-xs text-nes-white/60">
        <summary className="cursor-pointer text-nes-cyan">Keyboard reference</summary>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1">
          <dt className="font-bold text-nes-white">Z S X D C V G B H N J M</dt>
          <dd>Lower octave notes (C through B at current octave)</dd>
          <dt className="font-bold text-nes-white">Q 2 W 3 E R 5 T 6 Y 7 U</dt>
          <dd>Upper octave notes (octave + 1)</dd>
          <dt className="font-bold text-nes-white">Arrows</dt>
          <dd>Move cursor (row / channel)</dd>
          <dt className="font-bold text-nes-white">Page Up/Down</dt>
          <dd>Jump 16 rows</dd>
          <dt className="font-bold text-nes-white">Backspace / Delete</dt>
          <dd>Clear cell</dd>
          <dt className="font-bold text-nes-white">Comma (,)</dt>
          <dd>Note off (OFF)</dd>
          <dt className="font-bold text-nes-white">+ / −</dt>
          <dd>Octave up / down</dd>
          <dt className="font-bold text-nes-white">Space</dt>
          <dd>Play / stop</dd>
        </dl>
        <p className="mt-2 text-[10px] text-nes-white/40">
          <strong className="text-nes-white/70">Edit mode (●&nbsp;REC)</strong> controls whether
          typing writes to the pattern. OFF = typing only previews the note (no cell change).
          ON = preview + write + auto-advance one row. DPCM column is read-only until instrument
          support lands. Tempo slider is live. Play starts from the cursor, ⏮ starts from order
          00 row 00. Cell edits are heard on the next row that fires; order-list / pattern-add
          changes need stop+play. Notes hold until you write OFF (comma) on a later row — FT2
          convention, not a bug.
        </p>
      </details>
    </section>
  );
}
